import type { ProgramAccount } from "@coral-xyz/anchor";
import { useQuery } from "@tanstack/react-query";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useUsersVoteRecords } from "hooks/useUsersVoteRecords";
import { useValidatorScores } from "hooks/useValidatorsScores";
import { fetchTotalMSOLDirectedStakeVotes } from "services/marinade/fetchTotalMSOLDirectedStakeVotes";
import { fetchTotalVeMNDEDirectedStakeVotes } from "services/marinade/fetchTotalVeMNDEDirectedStakeVotes";
import { fetchTvlInfo } from "services/marinade/fetchTvlInfo";
import { fetchUserDirectedStakeVotes } from "services/marinade/fetchUsersDirectedStakeVotes";
import { fetchUsersVotingPower } from "services/marinade/fetchUsersVotingPower";
import type { ValidatorScore } from "types/validator-info";

import { useWalletInfo } from "./WalletInfoContext";

const DirectStakeContext = createContext<{
  scores?: ValidatorScore[];
  mSOLGovernedStake?: number;
  voteRecord?: ProgramAccount;
  mSOLDirectPower?: number;
  veMNDEDirectPower?: number;
  usersMSOLDirectedStakeVotes?: number;
  usersMNDEDirectedStakeVotes?: number;
  isLoadingOnChainData: boolean;
  setIsLoadingOnChainData: (v: boolean) => void;
  refetchVoteRecord: () => void;
}>({
  scores: undefined,
  mSOLGovernedStake: undefined,
  voteRecord: undefined,
  mSOLDirectPower: undefined,
  veMNDEDirectPower: undefined,
  usersMSOLDirectedStakeVotes: undefined,
  usersMNDEDirectedStakeVotes: undefined,
  isLoadingOnChainData: false,
  setIsLoadingOnChainData: () => {
    throw new Error("Not implemented!");
  },
  refetchVoteRecord: () => {
    throw new Error("Not implemented!");
  },
});

export const DirectStakeProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [mSOLDirectPower, setMSOLDirectPower] = useState<number>(0);
  const [veMNDEDirectPower, setVeMNDEDirectPower] = useState<number>(0);
  const [mSOLGovernedStake, setMSOLGovernedStake] = useState<number>(0);

  const { walletAddress } = useWalletInfo();
  const { scores } = useValidatorScores();
  const {
    data: record,
    isLoadingOnChainData,
    setIsLoadingOnChainData,
    refetch: refetchVoteRecord,
  } = useUsersVoteRecords();

  const { data: tvlInfo } = useQuery([`tvl-Info`], fetchTvlInfo, {
    refetchInterval: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const { data: usersMSOLDirectedStakeVotes } = useQuery(
    ["directed-stake-msol-votes", walletAddress?.toString()],
    async () => fetchUserDirectedStakeVotes(walletAddress?.toString() ?? ""),
    {
      enabled: !!walletAddress,
      refetchInterval: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { data: totalMSOLDirectedStakeVotes } = useQuery(
    ["total-directed-stake-msol-votes"],
    async () => fetchTotalMSOLDirectedStakeVotes(),
    {
      enabled: !!walletAddress,
      refetchInterval: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { data: totalMNDEDirectedStakeVotes } = useQuery(
    ["total-directed-stake-vemnde-votes"],
    async () => fetchTotalVeMNDEDirectedStakeVotes(),
    {
      enabled: !!walletAddress,
      refetchInterval: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { data: usersMNDEDirectedStakeVotes } = useQuery(
    ["directed-stake-mnde-votes", walletAddress?.toString()],
    async () => fetchUsersVotingPower(walletAddress?.toString() ?? ""),
    {
      enabled: !!walletAddress,
      refetchInterval: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  useEffect(() => {
    if (!tvlInfo) return;

    const directedStake = (tvlInfo.total_sol - tvlInfo.liquidity_sol) * 0.2;
    setMSOLGovernedStake(directedStake);

    if (totalMSOLDirectedStakeVotes)
      setMSOLDirectPower(directedStake / totalMSOLDirectedStakeVotes);

    if (totalMNDEDirectedStakeVotes)
      setVeMNDEDirectPower(directedStake / totalMNDEDirectedStakeVotes);
  }, [tvlInfo, totalMSOLDirectedStakeVotes, totalMNDEDirectedStakeVotes]);

  const value = useMemo(
    () => ({
      scores,
      mSOLGovernedStake,
      mSOLDirectPower,
      veMNDEDirectPower,
      usersMSOLDirectedStakeVotes,
      usersMNDEDirectedStakeVotes,
      voteRecord: record?.voteRecord,
      isLoadingOnChainData,
      setIsLoadingOnChainData,
      refetchVoteRecord,
    }),
    [
      scores,
      mSOLGovernedStake,
      mSOLDirectPower,
      veMNDEDirectPower,
      usersMSOLDirectedStakeVotes,
      usersMNDEDirectedStakeVotes,
      record,
      isLoadingOnChainData,
      setIsLoadingOnChainData,
      refetchVoteRecord,
    ]
  );

  return (
    <DirectStakeContext.Provider value={value}>
      {children}
    </DirectStakeContext.Provider>
  );
};

export function useDirectStakeInfos() {
  return useContext(DirectStakeContext);
}
